import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import {
  useDeleteReferral,
  useGetReferral,
  useUpdateReferral,
} from "@/referrals/query";
import { IReferral, ReferralUpdateParams } from "@/referrals/referral";
import { TableSidebar } from "@/referrals/referral-table-view/table-sidebar";
import { ReferralHistorySidebarSkeleton } from "./referral-history-sidebar-skeleton";

interface ReferralHistorySidebarProps {
  referralId: string;
  onClose: () => void;
  onPayout: (referral: IReferral) => void;
}

const ReferralHistorySidebar = ({
  referralId,
  onClose,
  onPayout,
}: ReferralHistorySidebarProps) => {
  const { businessId } = useParams();
  const { mutate: updateReferral } = useUpdateReferral(businessId);
  const { mutate: deleteReferral } = useDeleteReferral(businessId);

  const { data: referral, isLoading } = useGetReferral(businessId, referralId);

  const handleUpdate = (updates: Partial<ReferralUpdateParams>) => {
    updateReferral(
      { id: referralId, updates },
      {
        onSuccess: () => {
          toast.success("Referral updated");
        },
        onError: (error) => {
          toast.error("Failed to update referral", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleDelete = useCallback(() => {
    deleteReferral(
      {
        id: referralId,
      },
      {
        onSuccess: () => {
          toast.success("Referral deleted successfully");
          onClose();
        },
        onError: (error) => {
          toast.error("Failed to delete referral", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  }, [referralId, deleteReferral, toast]);

  if (isLoading) {
    return <ReferralHistorySidebarSkeleton />;
  }

  return (
    <TableSidebar
      referral={referral}
      onClose={onClose}
      onUpdate={handleUpdate}
      onPayout={referral ? () => onPayout(referral) : () => {}}
      onDelete={handleDelete}
    />
  );
};
export default ReferralHistorySidebar;
